import { Form } from 'components/Form';
import styled, { css, rem, spacingBase, tablet } from 'theme';

export const SearchForm = styled.form`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

export const SearchContainer = styled.div`
  position: absolute;
  height: ${rem(64)};
  width: 100%;
  display: flex;
  flex-direction: column;
  border-top: ${rem(1)} solid ${(p) => p.theme.colors.grayD20};
  background-color: ${(p) => p.theme.colors.white};

  padding: ${rem(0)} ${() => spacingBase};

  ${tablet(css`
    padding: ${rem(0)} ${rem(182)};
  `)}
`;

interface SearchInputProps {
  hasResults: boolean;
}

export const SearchInput = styled.div<SearchInputProps>`
  display: flex;
  flex: 1 0 0;
  flex-direction: row;
  align-items: center;
  align-self: stretch;
  justify-content: center;
  background: ${(p) => p.theme.colors.white};
  &:has(input:focus) {
    outline: 1px solid ${(p) => p.theme.colors.grayD20};
  }

  ${(p) =>
    p.hasResults &&
    css`
      border: ${rem(1)} solid ${(p) => p.theme.colors.grayD20};
      border-radius: ${rem(4)} ${rem(4)} 0 0;
      border-bottom: none;
    `}
  ${(p) =>
    !p.hasResults &&
    css`
      border: ${rem(1)} solid ${(p) => p.theme.colors.grayD20};
      border-radius: ${rem(4)};
    `}

  gap: ${() => spacingBase};
  padding: ${rem(4)} ${rem(8)} ${rem(4)} ${rem(16)};
  height: ${rem(42)};
  margin: ${rem(11)} 0;

  ${tablet(css`
    gap: ${rem(10)};
  `)}
`;

export const SearchResult = styled.div`
  display: flex;
  flex-direction: column;

  border: ${rem(1)} solid ${(p) => p.theme.colors.grayD30};
  border-radius: 0 0 ${rem(4)} ${rem(4)};
  border-top: none;
  background: ${(p) => p.theme.colors.white};
  box-shadow: 0 ${rem(4)} ${rem(4)} 0 #00000040;
  padding: 0 ${rem(16)} ${rem(16)};

  position: relative;
  top: ${rem(-11)};
  width: 100%;

  a {
    color: ${(p) => p.theme.colors.black};
    font-weight: ${(p) => p.theme.fonts.weights.bold};
    text-decoration: ${(p) => p.theme.buttons.hoverTextDecoration};
  }
`;

export const SearchFeedbackWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${rem(8)};

  p {
    font-weight: ${(p) => p.theme.fonts.weights.bold};
  }
`;

export const Disclaimer = styled.div`
  font-size: ${rem(14)};
  font-style: italic;
  font-weight: ${(p) => p.theme.fonts.weights.light};
  line-height: ${rem(16)};
  text-align: left;
  color: ${(p) => p.theme.colors.gray600};
`;

export const SeparationLine = styled.div`
  border-top: ${rem(1)} solid ${(p) => p.theme.colors.grayL10};
  margin: ${rem(16)} 0;
`;

export const Search = styled('input')`
  width: 100%;
  height: 100%;
  border: none;
  outline: none;
`;

interface SubmitButtonProps {
  active: boolean;
}

export const SubmitButton = styled.button<SubmitButtonProps>`
  display: block;
  color: ${(p) => p.theme.colors.black};
  cursor: pointer;

  padding: ${rem(8)};
  border: none;
  border-radius: ${rem(4)};
  background-color: ${(p) => p.theme.colors.grayL30};
  ${(p) =>
    p.active &&
    css`
      background-color: ${(p) => p.theme.colors.primary};
    `}

  svg {
    width: ${rem(18)};
    height: ${rem(18)};
    vertical-align: bottom;
    transform: rotate(-90deg);
  }
`;

export const ButtonIcon = styled('button')`
  display: block;
  color: ${(p) => p.theme.colors.black};
  cursor: pointer;

  padding: ${rem(8)};
  border: none;
  background-color: ${(p) => p.theme.colors.white};

  svg {
    width: ${rem(18)};
    height: ${rem(18)};
    vertical-align: bottom;
  }
`;

export enum ButtonVariant {
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}
interface FeedbackButtonProps {
  variant: ButtonVariant;
  active: boolean;
}

enum ColorOpacity {
  LIGHT = '33', // 20%
}

export const FeedbackButtonIcon = styled('button')<FeedbackButtonProps>`
  display: block;
  cursor: pointer;
  width: ${rem(44)};
  height: ${rem(44)};
  border-radius: ${rem(24)};
  padding: ${rem(8)};
  border: 1px solid
    ${(p) => (p.variant === ButtonVariant.SUCCESS ? `${p.theme.colors.success}` : `${p.theme.colors.danger}`)};
  background-color: ${(p) => {
    if (p.active) {
      return p.variant === ButtonVariant.SUCCESS ? `${p.theme.colors.success}` : `${p.theme.colors.danger}`;
    }
    return 'transparent';
  }};
  svg {
    width: ${rem(24)};
    height: ${rem(24)};
    fill: ${(p) => {
      if (p.active) {
        return p.theme.colors.white;
      }
      return p.variant === ButtonVariant.SUCCESS ? `${p.theme.colors.success}` : `${p.theme.colors.danger}`;
    }};
    vertical-align: bottom;
  }

  &:hover:enabled {
    background-color: ${(p) =>
      p.variant === ButtonVariant.SUCCESS
        ? `${p.theme.colors.success}${ColorOpacity.LIGHT}`
        : `${p.theme.colors.danger}${ColorOpacity.LIGHT}`};
  }

  &:active:enabled,
  &:focus:enabled {
    background-color: ${(p) =>
      p.variant === ButtonVariant.SUCCESS ? `${p.theme.colors.success}` : `${p.theme.colors.danger}`};

    svg {
      fill: ${(p) => p.theme.colors.white};
    }
  }

  &:disabled {
    cursor: default;
  }
`;
export const ExtendedFeedbackForm = styled(Form)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  max-width: ${rem(367)};
`;
